import type { ButtonProps } from '@mui/material';
import { Button } from '@mui/material';
import { BILLING_CYCLE_INVOICE_TYPES, type BillingCycleInvoice } from '@weft/api-definition';
import { VyIcon } from '@weft/icons';
import { isNotNullOrUndefined, percentage, validateNotEmptyArray, withDefault } from '@weft/utils';
import { dayjs } from '@weft/utils';
import { formatNumber } from '@weft/utils';
import { useBillingCycleInvoices } from 'apps/app/src/hooks/requests/use-billing-cycle-invoices/use-billing-cycle-invoices';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import React, { useCallback } from 'react';

type ExportToCsvButtonProps = {
  startDay: Date;
  endDay: Date;
} & ButtonProps;

export const ExportBillingCycleToCsv:React.FC<ExportToCsvButtonProps> = ({
  startDay,
  endDay,
  ...buttonProps
}) => {
  const regularBillingCycleInvoicesData = useBillingCycleInvoices({ pageSize: 10000, billingCycleInvoiceType: BILLING_CYCLE_INVOICE_TYPES.REGULAR }, {
    enabled: false,
  });
  const inspectionBillingCycleInvoicesData = useBillingCycleInvoices({ pageSize: 10000, billingCycleInvoiceType: BILLING_CYCLE_INVOICE_TYPES.INSPECTION }, {
    enabled: false,
  });

  const onExportClick = useCallback(async () => {
    const regularInvoices = await regularBillingCycleInvoicesData.refetch();
    const inspectionInvoices = await inspectionBillingCycleInvoicesData.refetch();

    handleExportCsvClick([...withDefault(regularInvoices.data?.invoices, []), ...withDefault(inspectionInvoices.data?.invoices, [])], startDay, endDay);
  }, [endDay, inspectionBillingCycleInvoicesData, regularBillingCycleInvoicesData, startDay]);

  return (
    <Button
      variant="outlined"
      size="large"
      {...buttonProps}
      sx={{
        columnGap: '8px',
      }}
      onClick={onExportClick}
    >
      Export
      <VyIcon icon="Save"/>
    </Button>
  );
};

function handleExportCsvClick(invoices: BillingCycleInvoice[] | undefined, startDay: Date, endDay: Date) {
  validateNotEmptyArray(invoices);
  const invoicesToExport = invoices.map(buildAggregatedInvoiceForCsv);
  const csv = Papa.unparse(invoicesToExport);
  const fileNameDayRange = formatDatesRange(startDay, endDay);

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const fileName = `Billing Cycle Report - ${fileNameDayRange}.csv`;

  saveAs(blob, fileName);
}

function formatDatesRange(startDay: Date, endDay: Date) {
  return dayjs(startDay).format('MM/DD/YYYY') + ' To ' + dayjs(endDay).format('MM/DD/YYYY');
}

function buildAggregatedInvoiceForCsv(invoice: BillingCycleInvoice) {
  const amountChange = invoice.revenueBreakdown.total - withDefault(invoice.previousInvoiceAmount, 0);
  const amountChangePercentage = percentage(amountChange, withDefault(invoice.previousInvoiceAmount, 0) );

  return {
    'Date': dayjs(invoice.billingCycle.startTime).format('MM/DD/YYYY'),
    'Customer': invoice.customerName,
    'ExternalID': invoice.customerExternalId,
    'Amount': formatNumber(invoice.revenueBreakdown.total),
    'Prev. Invoice Amount': isNotNullOrUndefined(invoice.previousInvoiceAmount) ? formatNumber(invoice.previousInvoiceAmount) : '--',
    'Amount Change (%)': amountChangePercentage === 0 ? '--' : formatNumber(amountChangePercentage) + '%',
    'Currency': invoice.currency,
    'ERP ID': invoice.erpId,
  };
}